import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  Grid,
  Button,
  Avatar,
  IconButton,
  Chip,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider
} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import { FirebaseContext, api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { FONT_FAMILY } from "../common/sharedFunctions";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CircularLoading from "../components/CircularLoading";

const MyProfile = () => {
  const { authRef, RecaptchaVerifier, unlink, linkWithPhoneNumber } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    updateProfile,
    updateWebProfileImage,
    updateProfileWithEmail,
    checkUserExists,
    requestMobileOtp,
    updateAuthMobile
  } = api;
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const languagedata = useSelector(state => state.languagedata);
  const [langSelection, setLangSelection] = useState(0);
  const [multiLanguage, setMultiLanguage] = useState();
  const fileInputRef = useRef();
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const settings = useSelector(state => state.settingsdata.settings);

  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    usertype: '',
    verifyId: '',
    profile_image: ''
  });

  const [otp, setOtp] = useState("");
  const [otpCalled, setOtpCalled] = useState(false);
  const [updateCalled, setUpdateCalled] = useState(false);
  const [confirmCodeFunction, setConfirmCodeFunction] = useState();

  useEffect(() => {
    if (languagedata.langlist) {
      let arr = Object.keys(languagedata.langlist);
      for (let i = 0; i < arr.length; i++) {
        if (auth.profile && auth.profile.lang && auth.profile.lang.langLocale && auth.profile.lang.langLocale === languagedata.langlist[arr[i]].langLocale) {
          setLangSelection(i);
        }
      }
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist, auth.profile]);

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setData({
        firstName: auth.profile.firstName || '',
        lastName: auth.profile.lastName || '',
        email: auth.profile.email || '',
        mobile: auth.profile.mobile || '',
        usertype: auth.profile.usertype || 'admin',
        profile_image: auth.profile.profile_image || '',
        verifyId: auth.profile.verifyId || ''
      });
      if (updateCalled) {
        setLoading(false);
        setCommonAlert({ open: true, msg: t('profile_updated', { defaultValue: 'Profile updated successfully!' }) });
        setUpdateCalled(false);
      }
    }
  }, [auth.profile, t, updateCalled]);

  const updateData = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const completeSubmit = () => {
    setUpdateCalled(true);
    let arr = Object.keys(languagedata.langlist || {});
    if (multiLanguage && arr.length > 0 && multiLanguage[arr[langSelection]]) {
      localStorage.setItem('lang', JSON.stringify({
        langLocale: multiLanguage[arr[langSelection]].langLocale,
        dateLocale: multiLanguage[arr[langSelection]].dateLocale
      }));
    }

    dispatch(updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      mobile: data.mobile,
      verifyId: data.verifyId,
      lang: (multiLanguage && arr.length > 0 && multiLanguage[arr[langSelection]]) ? {
        langLocale: multiLanguage[arr[langSelection]].langLocale,
        dateLocale: multiLanguage[arr[langSelection]].dateLocale
      } : undefined
    }));

    if (selectedImage) {
      updateWebProfileImage(selectedImage);
    }

    const lang = multiLanguage && arr && arr[langSelection] ? multiLanguage[arr[langSelection]] : null;
    if (lang && lang.keyValuePairs && typeof lang.keyValuePairs === "object") {
      i18n.addResourceBundle(lang.langLocale, 'translations', lang.keyValuePairs, true, true);
    }
    if (lang && lang.langLocale) {
      i18n.changeLanguage(lang.langLocale);
      moment.locale(lang.dateLocale || lang.langLocale);
    }
    setSelectedImage(null);
    setCommonAlert({ open: true, msg: t('profile_updated', { defaultValue: 'Profile updated successfully!' }) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (data.email !== auth.profile?.email && data.mobile !== auth.profile?.mobile) {
      setCommonAlert({ open: true, msg: t('update_any', { defaultValue: 'Please update email or mobile one at a time.' }) });
    } else {
      if (data.firstName && data.firstName.length > 0 && data.lastName && data.lastName.length > 0) {
        if (data.email !== auth.profile?.email) {
          const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          if (re.test(data.email)) {
            setLoading(true);
            checkUserExists({ email: data.email }).then((res) => {
              if (res.users && res.users.length > 0) {
                setLoading(false);
                alert(t('user_exists'));
              } else if (res.error) {
                setLoading(false);
                alert(t('email_or_mobile_issue'));
              } else {
                data['uid'] = auth.profile.uid;
                dispatch(updateProfileWithEmail(data));
                setLoading(false);
                setCommonAlert({ open: true, msg: t('profile_updated') });
              }
            });
          } else {
            setCommonAlert({ open: true, msg: t('proper_email') });
          }
        } else if (data.mobile !== auth.profile?.mobile) {
          if (data.mobile && data.mobile.length > 6) {
            setLoading(true);
            checkUserExists({ mobile: data.mobile }).then(async (res) => {
              if (res.users && res.users.length > 0) {
                setLoading(false);
                alert(t('user_exists'));
              } else if (res.error) {
                setLoading(false);
                alert(t('email_or_mobile_issue'));
              } else {
                setLoading(false);
                setOtpCalled(true);

                if (settings?.customMobileOTP) {
                  dispatch(requestMobileOtp(data.mobile));
                } else {
                  window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', {
                    'size': 'invisible'
                  });

                  if (auth.profile.mobile && auth.profile.mobile.length > 6) {
                    await unlink(authRef().currentUser, "phone");
                  }

                  const linkObj = await linkWithPhoneNumber(authRef().currentUser, data.mobile, window.recaptchaVerifier);
                  setConfirmCodeFunction(linkObj);
                }
              }
            });
          } else {
            setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
          }
        } else {
          setLoading(true);
          completeSubmit();
        }
      } else {
        setCommonAlert({ open: true, msg: t('no_details_error') });
      }
    }
  };

  const updateOtp = (e) => {
    setOtp(e.target.value);
  };

  const handleVerify = async () => {
    if (otp && otp.length === 6 && !isNaN(otp)) {
      setLoading(true);
      if (settings?.customMobileOTP) {
        const res = await updateAuthMobile(data.mobile, otp);
        if (res.success) {
          completeSubmit();
        } else {
          setOtp('');
          setLoading(false);
          if (res.error === 'Error updating user') {
            setCommonAlert({ open: true, msg: t('user_exists') });
          } else {
            setCommonAlert({ open: true, msg: t('otp_validate_error') });
          }
        }
      } else {
        if (confirmCodeFunction) {
          confirmCodeFunction.confirm(otp).then(() => {
            completeSubmit();
          }).catch(() => {
            setOtp('');
            setLoading(false);
            if (window.recaptchaVerifier) window.recaptchaVerifier.clear();
            setCommonAlert({ open: true, msg: t('otp_validate_error') });
          });
        }
      }
    } else {
      setOtp('');
      setCommonAlert({ open: true, msg: t('otp_validate_error') });
    }
    setOtpCalled(false);
    window.recaptchaVerifier = null;
  };

  const handleClose = () => {
    setOtp('');
    setOtpCalled(false);
    window.recaptchaVerifier = null;
  };

  const handleCommonAlertClose = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCommonAlert({ open: false, msg: '' });
  };

  const handleLanguageSelect = (event) => {
    setLangSelection(event.target.value);
  };

  const profileImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  return (
    auth.loading ? <CircularLoading /> :
      <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, sm: 3, md: 4 }, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
        <div id="sign-in-button" />
        <Card
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
            boxShadow: '0 4px 24px -4px rgba(15, 23, 42, 0.06)'
          }}
        >
          <Box sx={{ height: 110, backgroundColor: '#0B1120', position: 'relative', px: 4 }}>
            <Box sx={{ position: 'absolute', top: 16, right: 20 }}>
              <Chip
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#10B981 !important' }} />}
                label={data.usertype ? data.usertype.toUpperCase() : 'SUPER ADMIN'}
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  color: '#FFFFFF',
                  backdropFilter: 'blur(8px)',
                  letterSpacing: '0.5px'
                }}
              />
            </Box>
          </Box>

          <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 4, pt: 0, position: 'relative' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-end' }, gap: 2.5, mt: -6, mb: 3 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={selectedImage ? URL.createObjectURL(selectedImage) : (data.profile_image || require("../assets/img/profilePic.png"))}
                  alt="Profile"
                  sx={{
                    width: 100,
                    height: 100,
                    border: '4px solid #FFFFFF',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    backgroundColor: '#F1F5F9'
                  }}
                />
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#E5232A',
                    color: '#FFFFFF',
                    width: 32,
                    height: 32,
                    boxShadow: '0 2px 8px rgba(229, 35, 42, 0.4)',
                    '&:hover': { backgroundColor: '#C81E24' }
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <input onChange={profileImageChange} multiple={false} ref={fileInputRef} type="file" accept="image/*" hidden />
              </Box>

              <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '1.35rem' }}>
                  {data.firstName} {data.lastName}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.84rem' }}>
                  {data.email} • {data.mobile}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3.5, borderColor: '#F1F5F9' }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#334155', display: 'block', mb: 0.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('firstname', { defaultValue: 'First Name' })}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="firstName"
                    name="firstName"
                    value={data.firstName}
                    onChange={updateData}
                    required
                    InputProps={{
                      sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC', fontSize: '0.9rem' }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#334155', display: 'block', mb: 0.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('lastname', { defaultValue: 'Last Name' })}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="lastName"
                    name="lastName"
                    value={data.lastName}
                    onChange={updateData}
                    required
                    InputProps={{
                      sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC', fontSize: '0.9rem' }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#334155', display: 'block', mb: 0.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('email', { defaultValue: 'Email Address' })}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="email"
                    name="email"
                    value={data.email}
                    onChange={updateData}
                    required
                    InputProps={{
                      sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC', fontSize: '0.9rem' }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#334155', display: 'block', mb: 0.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('phone', { defaultValue: 'Phone Number' })}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="mobile"
                    name="mobile"
                    value={data.mobile}
                    onChange={updateData}
                    required
                    dir="ltr"
                    InputProps={{
                      sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC', fontSize: '0.9rem' }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#334155', display: 'block', mb: 0.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('usertype', { defaultValue: 'Role / Access Level' })}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="usertype"
                    name="usertype"
                    value={data.usertype?.toUpperCase()}
                    disabled
                    InputProps={{
                      sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F1F5F9', fontSize: '0.9rem', fontWeight: 600 }
                    }}
                  />
                </Grid>

                {multiLanguage && Object.keys(multiLanguage).length > 1 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#334155', display: 'block', mb: 0.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('preferred_language', { defaultValue: 'Preferred Language' })}
                    </Typography>
                    <Select
                      fullWidth
                      variant="outlined"
                      value={langSelection}
                      onChange={handleLanguageSelect}
                      sx={{ fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC', fontSize: '0.9rem' }}
                    >
                      {Object.keys(multiLanguage).map((key, index) => (
                        <MenuItem key={key} value={index}>
                          <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem' }}>{multiLanguage[key].langName}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    fontFamily: FONT_FAMILY,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: 1.2,
                    px: 4,
                    borderRadius: '10px',
                    backgroundColor: '#E5232A',
                    boxShadow: '0 4px 14px rgba(229, 35, 42, 0.35)',
                    '&:hover': { backgroundColor: '#C81E24' }
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : t('save_changes', { defaultValue: 'Save Profile Changes' })}
                </Button>
              </Box>
            </form>
          </Box>
        </Card>

        <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
          {commonAlert.msg}
        </AlertDialog>

        <Dialog open={otpCalled} onClose={handleClose}>
          <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>{t('otp_here', { defaultValue: 'Enter SMS Verification Code' })}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="otp"
              label={t('otp', { defaultValue: '6-digit OTP' })}
              type="number"
              fullWidth
              variant="outlined"
              onChange={updateOtp}
              value={otp}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose} sx={{ fontFamily: FONT_FAMILY }}>{t('cancel')}</Button>
            <Button onClick={handleVerify} variant="contained" sx={{ fontFamily: FONT_FAMILY, backgroundColor: '#E5232A' }}>{t('verify_otp')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default MyProfile;